import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRightIcon } from "@/shared/assets/icons";
import { Button, Input, SearchableSelect, Select, ToggleButton } from "@/shared/ui";
import { MONTHS_EN, MONTHS_KK, MONTHS_RU } from "@/shared/consts/months";
import { extractErrorMessage, type Locale } from "@/shared/utils";
import { EXPERIENCE_SECTOR_OPTIONS } from "../../../../consts";
import { useCreateExperienceMutation, useUpdateExperienceMutation } from "./api";
import type { ExperienceRecord } from "./types";
import { buildISODate, parseMonthYear } from "./utils";
import { experienceSchema, type ExperienceFormValues } from "./validation";

const WORK_FORMAT_VALUES = ["onsite", "remote", "hybrid"] as const;

interface YearOption {
  label: string;
  value: number;
}

function generateYearOptions(): YearOption[] {
  const currentYear = new Date().getFullYear();
  const years: YearOption[] = [];
  for (let year = currentYear; year >= 1970; year--) {
    years.push({ label: String(year), value: year });
  }
  return years;
}

const YEAR_OPTIONS = generateYearOptions();

function getMonthOptions(locale: Locale) {
  const months = locale === "kk" ? MONTHS_KK : locale === "en" ? MONTHS_EN : MONTHS_RU;
  return months.map((name, index) => ({ label: name, value: index + 1 }));
}

interface Props {
  token: string;
  record?: ExperienceRecord;
  onCancel: () => void;
  onSuccess: () => void;
  onToast: (toast: { text: string; color: "positive" | "negative" | "notice" | "grey" }) => void;
}

function buildDefaults(record?: ExperienceRecord): ExperienceFormValues {
  const begin = parseMonthYear(record?.beginning_date);
  const end = parseMonthYear(record?.end_date);
  return {
    organization_name: record?.organization_name ?? "",
    speciality: record?.speciality ?? "",
    work_position: record?.work_position ?? "",
    work_format: record?.work_format ?? "",
    work_address: record?.work_address ?? "",
    begin_month: begin.month,
    begin_year: begin.year,
    end_month: end.month,
    end_year: end.year,
    is_current: record?.is_current ?? false,
  };
}

export default function ExperienceForm({ token, record, onCancel, onSuccess, onToast }: Props) {
  const { i18n, t } = useTranslation("ApplyApplicationPage");
  const locale = (i18n.language as Locale) || "ru";
  const isEdit = !!record;

  const [createExperience, { isLoading: isCreating }] = useCreateExperienceMutation();
  const [updateExperience, { isLoading: isUpdating }] = useUpdateExperienceMutation();
  const isSaving = isCreating || isUpdating;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ExperienceFormValues>({
    resolver: zodResolver(experienceSchema),
    defaultValues: buildDefaults(record),
  });

  const speciality = watch("speciality");
  const workFormat = watch("work_format");
  const beginMonth = watch("begin_month");
  const beginYear = watch("begin_year");
  const endMonth = watch("end_month");
  const endYear = watch("end_year");
  const isCurrent = watch("is_current");

  const monthOptions = getMonthOptions(locale);
  const sectorOptions = EXPERIENCE_SECTOR_OPTIONS.map(function toOption(option) {
    return { label: option.value, value: option.value };
  });
  const workFormatOptions = WORK_FORMAT_VALUES.map(function toFormatOption(value) {
    return { value, label: t(`experience.work_format.${value}`) };
  });

  function handleCurrentChange(checked: boolean) {
    setValue("is_current", checked, { shouldValidate: true });
    if (checked) {
      setValue("end_month", null, { shouldValidate: true });
      setValue("end_year", null, { shouldValidate: true });
    }
  }

  async function onSubmit(values: ExperienceFormValues) {
    if (isSaving) return;

    const body = {
      organization_name: values.organization_name,
      speciality: values.speciality,
      work_position: values.work_position,
      work_format: values.work_format,
      work_address: values.work_address,
      beginning_date: buildISODate(values.begin_month as number, values.begin_year as number),
      end_date:
        values.is_current || values.end_month == null || values.end_year == null
          ? null
          : buildISODate(values.end_month, values.end_year),
      is_current: values.is_current,
    };

    try {
      if (isEdit && record) {
        await updateExperience({ token, id: record.id, body }).unwrap();
        onToast({ color: "positive", text: "experience.toast.update_success" });
      } else {
        await createExperience({ token, body }).unwrap();
        onToast({ color: "positive", text: "experience.toast.success" });
      }
      onSuccess();
    } catch (err: unknown) {
      onToast({ color: "negative", text: extractErrorMessage(err, "experience.toast.error", t) });
    }
  }

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label={t("experience.organization.label")}
          hasRequiredLabel
          placeholder={t("experience.organization.placeholder")}
          {...register("organization_name")}
          error={errors.organization_name?.message ? t(errors.organization_name.message) : undefined}
        />
        <Select
          label={t("experience.work_format.label")}
          hasRequiredLabel
          options={workFormatOptions}
          value={workFormat}
          onChange={(v) => setValue("work_format", (v as string) ?? "", { shouldValidate: true })}
          placeholder={t("experience.work_format.placeholder")}
          error={errors.work_format?.message ? t(errors.work_format.message) : undefined}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select
          label={t("experience.speciality.label")}
          hasRequiredLabel
          options={sectorOptions}
          value={speciality}
          onChange={(v) => setValue("speciality", (v as string) ?? "", { shouldValidate: true })}
          placeholder={t("experience.speciality.placeholder")}
          error={errors.speciality?.message ? t(errors.speciality.message) : undefined}
        />
        <Input
          label={t("experience.work_position.label")}
          hasRequiredLabel
          placeholder={t("experience.work_position.placeholder")}
          {...register("work_position")}
          error={errors.work_position?.message ? t(errors.work_position.message) : undefined}
        />
      </div>

      <Input
        label={t("experience.work_address.label")}
        hasRequiredLabel
        maxLength={500}
        placeholder={t("experience.work_address.placeholder")}
        {...register("work_address")}
        error={errors.work_address?.message ? t(errors.work_address.message) : undefined}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold content-base-primary">
            {t("experience.begin.label")}
            <span className="content-action-negative ml-1">*</span>
          </label>
          <div className="grid grid-cols-[3fr_2fr] gap-4">
            <Select
              options={monthOptions}
              value={beginMonth}
              onChange={(v) => setValue("begin_month", typeof v === "number" ? v : null, { shouldValidate: true })}
              placeholder={t("experience.begin.month_placeholder")}
            />
            <SearchableSelect<YearOption>
              options={YEAR_OPTIONS}
              value={YEAR_OPTIONS.find((y) => y.value === beginYear) ?? null}
              onChange={(option) => setValue("begin_year", option?.value ?? null, { shouldValidate: true })}
              searchKeys={["label"]}
              displayKey="label"
              hideSearchIcon
              placeholder={t("experience.begin.year_placeholder")}
            />
          </div>
          {errors.begin_month?.message && (
            <p className="text-body-regular-xs content-action-negative">{t(errors.begin_month.message as string)}</p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold content-base-primary">{t("experience.end.label")}</label>
          <div className="grid grid-cols-[3fr_2fr] gap-4">
            <Select
              options={monthOptions}
              value={endMonth}
              onChange={(v) => setValue("end_month", typeof v === "number" ? v : null, { shouldValidate: true })}
              placeholder={t("experience.end.month_placeholder")}
              disabled={isCurrent}
            />
            <SearchableSelect<YearOption>
              options={YEAR_OPTIONS}
              value={YEAR_OPTIONS.find((y) => y.value === endYear) ?? null}
              onChange={(option) => setValue("end_year", option?.value ?? null, { shouldValidate: true })}
              searchKeys={["label"]}
              displayKey="label"
              hideSearchIcon
              placeholder={t("experience.end.year_placeholder")}
              disabled={isCurrent}
            />
          </div>
          {errors.end_month?.message && (
            <p className="text-body-regular-xs content-action-negative">{t(errors.end_month.message as string)}</p>
          )}
          <div className="flex items-center justify-between gap-4 pt-1">
            <span className="text-label-sm content-base-secondary">{t("experience.working_now")}</span>
            <ToggleButton checked={isCurrent} onChange={handleCurrentChange} />
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-4 gap-3">
        <Button variant="secondary" size="lg" onClick={onCancel} disabled={isSaving} type="button">
          {t("experience.cancel")}
        </Button>
        <Button variant="primary" size="lg" type="submit" disabled={isSaving}>
          {isSaving ? t("experience.saving") : isEdit ? t("experience.save_changes") : t("experience.save")}
          {!isSaving && (
            <span className="text-white">
              <ArrowRightIcon />
            </span>
          )}
        </Button>
      </div>
    </form>
  );
}
